import { redirect } from "@tanstack/react-router";
import { signOut } from "firebase/auth";

import { HOME_PATH } from "@/config/constants";
import { auth } from "@/lib/firebase/client";

// Shared beforeLoad guard to enforce authentication and preserve redirect
export async function beforeAuthGuard({
  location,
}: {
  location: { href: string; pathname: string };
}) {
  // Ensure Firebase auth state is initialized before checking currentUser
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  // If user is not authenticated after auth state is ready, choose destination and redirect
  if (!auth.currentUser) {
    const pathname = location.pathname;

    // Decide where to send unauthenticated users based on origin
    const shouldRegister =
      pathname.startsWith("/checkout") || pathname.startsWith("/join");

    throw redirect({
      to: shouldRegister ? "/register" : "/signin",
      search: {
        redirect: location.href,
      },
    });
  }

  // Defense-in-depth: a patient account must never reach the clinician app shell.
  // With separate Firebase projects a patient token can't authenticate here at all
  // (wrong project), so this normally never fires — but it catches single-project
  // fallback or a misconfigured build before any clinician UI is exposed.
  const tokenResult = await auth.currentUser.getIdTokenResult();
  if (tokenResult.claims.is_patient === true) {
    await signOut(auth);
    throw redirect({ to: "/signin", search: { redirect: location.href } });
  }

  // The second factor. `signInWithEmailAndPassword` produces a perfectly valid
  // Firebase session *before* the emailed/TOTP code is verified, and the backend
  // rejects that session on every org-scoped endpoint with 403 MFA_REQUIRED. Without
  // this check such a user reaches the app shell and every request fails.
  //
  // Same predicate as the server's `FirebaseClaims::mfa_satisfied`: both halves are
  // required, and it fails closed.
  if (!mfaSatisfied(tokenResult.claims)) {
    throw redirect({ to: "/signin", search: { redirect: location.href } });
  }
}

/**
 * Has this token cleared the second factor, and is that clearance still live?
 *
 * Mirrors `FirebaseClaims::mfa_satisfied` in `apps/app-api/src/core/firebase.rs`.
 * `mfa_exp` is epoch ms marking the end of the 30-day trusted-device window.
 */
export function mfaSatisfied(claims: Record<string, unknown>): boolean {
  if (claims.mfa_ok !== true) return false;
  const exp = Number(claims.mfa_exp);
  return Number.isFinite(exp) && exp > Date.now();
}

// (The patient-portal guard lived here; patient auth now lives entirely in the
// separate apps/portal SPA, so it has been removed.)

// Shared beforeLoad guard to redirect authenticated users away from guest-only routes
export async function beforeGuestGuard() {
  // Ensure Firebase auth state is initialized before checking currentUser
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  if (!auth.currentUser) return;

  // Only bounce a *fully* authenticated user. A password-authenticated session
  // that has not yet cleared the second factor legitimately sits on /signin —
  // that is where the code entry step renders — so redirecting it would make
  // signing in impossible.
  //
  // The claim read is resolved before any throw so a token-refresh failure can't
  // be confused with the redirect (which TanStack Router signals by throwing).
  let cleared = false;
  try {
    const tokenResult = await auth.currentUser.getIdTokenResult();
    cleared = mfaSatisfied(tokenResult.claims);
  } catch {
    // Offline or refresh failed — leave the guest route rendered.
    return;
  }

  if (cleared) {
    throw redirect({ to: HOME_PATH });
  }
}
